import GlobalVue from 'vue'

/**
 * Prevent body scroll and overscroll.
 *
 * Based on: https://benfrain.com/preventing-body-scroll-for-modals-in-ios/
 *           https://stackoverflow.com/a/41601290
 *
 * Use in combination with:
 * html, body {overflow: hidden;}
 *
 * and: -webkit-overflow-scrolling: touch; for the element that should scroll.
 *
 * From: https://gist.github.com/thuijssoon/fd238517b487a45ce78d8f7ddfa7fee9
 */
export const BodyScroll = {
  install(Vue: any) {
    Vue.prototype.$bodyScroll = {
      _selector: false,
      _element: false,
      _clientY: undefined,

      /**
       * Prevent default unless within _selector
       *
       * @param  event object event
       * @return void
       */
      _preventBodyScroll(event: any) {
        if (this._element === false || !event.target.closest(this._selector)) {
          event.preventDefault()
        }
      },

      /**
       * Cache the clientY co-ordinates for
       * comparison
       *
       * @param  event object event
       * @return void
       */
      _captureClientY(event: any) {
        // only respond to a single touch
        if (event.targetTouches.length === 1) {
          this._clientY = event.targetTouches[0].clientY
        }
      },

      /**
       * Detect whether the element is at the top
       * or the bottom of their scroll and prevent
       * the user from scrolling beyond
       *
       * @param  event object event
       * @return void
       */
      _preventOverscroll(event: any) {
        // only respond to a single touch
        if (event.targetTouches.length !== 1) {
          return
        }

        const clientY = event.targetTouches[0].clientY - this._clientY

        // The element at the top of its scroll,
        // and the user scrolls down
        if (this._element.scrollTop === 0 && clientY > 0) {
          event.preventDefault()
        }

        // The element at the bottom of its scroll,
        // and the user scrolls up
        // https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollHeight#Problems_and_solutions
        if (
          this._element.scrollHeight - this._element.scrollTop <=
            this._element.clientHeight &&
          clientY < 0
        ) {
          event.preventDefault()
        }
      },

      disable(selector: any) {
        if (typeof selector !== 'undefined') {
          this._selector = selector
          this._element = document.querySelector(selector)
        }

        // Create binded functions
        this._captureClientY = this._captureClientY.bind(this)
        this._preventOverscroll = this._preventOverscroll.bind(this)
        this._preventBodyScroll = this._preventBodyScroll.bind(this)

        // @ts-ignore
        document.querySelector('body').classList.add('no-scroll')

        // TODO: this needs better implementation
        // if (this._element && this._element !== false) {
        //   this._element.setAttribute('style', '-webkit-overflow-scrolling: touch;')
        //   this._captureClientYEL = this._element.addEventListener('touchstart', this._captureClientY, false)
        //   this._preventOverscrollEL = this._element.addEventListener('touchmove', this._preventOverscroll, false)
        // }

        // document.querySelector('body').addEventListener('touchmove', this._preventBodyScroll, false)
      },

      enable(selector: any) {
        if (typeof selector !== 'undefined') {
          this._selector = selector
          this._element = document.querySelector(selector)
        }

        // TODO: this needs better implementation
        // if (this._element !== false) {
        //   this._element.removeEventListener('touchstart', this._captureClientY, false)
        //   this._element.removeEventListener('touchmove', this._preventOverscroll, false)
        // }
        //
        // document.querySelector('body').removeEventListener('touchmove', this._preventBodyScroll, false)

        this._selector = false
        this._element = false
        this._clientY = undefined

        // @ts-ignore
        document.querySelector('body').classList.remove('no-scroll')
      },

      get() {
        /**
         * Polyfills for Element.matches and Element.closest
         */
        /* istanbul ignore next */
        if (!Element.prototype.matches) {
          Element.prototype.matches =
            // @ts-ignore
            Element.prototype.msMatchesSelector ||
            Element.prototype.webkitMatchesSelector
        }

        /* istanbul ignore next */
        if (!Element.prototype.closest) {
          Element.prototype.closest = function(s: any) {
            const el = this
            let ancestor = this
            // @ts-ignore
            if (!document.documentElement.contains(el)) {
              return null
            }
            do {
              if (ancestor.matches(s)) {
                return ancestor
              }
              ancestor = ancestor.parentElement!
            } while (ancestor !== null)
            return null
          }
        }

        /* istanbul ignore next */
        return this
      },
    }
  },
}

// Declare module
export interface IBodyScroll {
  enable: any
  disable: any
}

declare module 'vue/types/vue' {
  export interface Vue {
    $bodyScroll: IBodyScroll
  }
}

// use plugin
GlobalVue.use(BodyScroll)
